import React from 'react';

import { Chip, Tooltip } from '@mui/material';

import EditOutlinedIcon from '@mui/icons-material/EditOutlined';

import AccountTreeOutlinedIcon from '@mui/icons-material/AccountTreeOutlined';

import TaskAltOutlinedIcon from '@mui/icons-material/TaskAltOutlined';

import RemoveCircleOutlineIcon from '@mui/icons-material/RemoveCircleOutline';

import {
  GridActionsCellItem,
  GridColDef,
  GridRenderCellParams,
} from '@mui/x-data-grid';

import { FDataGrid, FGridMoneyCell, FGridStatusChip } from '../grid';

type Props = {
  rows?: any[];

  loading?: boolean;

  /*

   * Opens ActivityForm in EDIT mode.

   */

  onEditActivity?: (row: any) => void;

  /*

   * Opens Task Planning for this seasonal ActivityPlan.

   */

  onPlanTasks?: (row: any) => void;

  /*

   * PLANNED -> READY.

   */

  onDeclareReady?: (row: any) => void;

  /*

   * Removes the Activity from the Planning Register:

   *

   * PLANNED / READY / RETURNED

   *          ->

   *        DRAFT

   *

   * Planned Tasks are removed.

   * The ActivityPlan itself remains.

   * This is NOT deletion of the Activity Master.

   */

  onRemoveActivity?: (row: any) => void;
};

const ValidationCell = (params: GridRenderCellParams) => {
  const row = params.row;

  const isValid = Boolean(row.isvalid);

  const blockingCount = Number(row.blockingcount ?? 0);

  return (
    <Chip
      size="small"
      variant="outlined"
      color={isValid ? 'success' : 'warning'}
      label={isValid ? 'Valid' : `${blockingCount} Blocking`}
    />
  );
};

export default class ActivityPlanningRegisterPanel extends React.PureComponent<Props> {
  private columns = (): GridColDef[] => [
    {
      field: 'activitycode',

      headerName: 'Activity',

      width: 145,

      valueGetter: (_value, row) =>
        row.activitycode ?? row.shc ?? row.code ?? '',
    },

    {
      field: 'activityname',

      headerName: 'Name',

      minWidth: 320,

      flex: 1,

      valueGetter: (_value, row) =>
        row.activityname ?? row.nam ?? row.name ?? '',
    },

    {
      field: 'statecode',

      headerName: 'State',

      width: 125,

      renderCell: FGridStatusChip,
    },

    {
      field: 'validation',

      headerName: 'Validation',

      width: 135,

      sortable: false,

      renderCell: ValidationCell,
    },

    {
      field: 'taskcount',

      headerName: 'Tasks',

      width: 90,

      type: 'number',

      valueGetter: (_value, row) => Number(row.taskcount ?? 0),
    },

    {
      field: 'budgetlinecount',

      headerName: 'Budget Lines',

      width: 115,

      type: 'number',

      valueGetter: (_value, row) => Number(row.budgetlinecount ?? 0),
    },

    {
      field: 'plannedamount',

      headerName: 'Planned Amount',

      width: 155,

      renderCell: FGridMoneyCell,

      valueGetter: (_value, row) =>
        Number(row.plannedamount ?? row.totalamount ?? 0),
    },

    {
      field: 'actions',

      type: 'actions',

      headerName: 'Actions',

      width: 170,

      getActions: (params) => {
        const row = params.row;

        const loading = Boolean(this.props.loading);

        const canEdit = Boolean(row.canedit);

        const canReady = Boolean(row.canready);

        const isReady =
          Boolean(row.isready) ||
          String(row.statecode ?? '').toUpperCase() === 'READY';

        return [
          <Tooltip
            key="edit"
            title={
              canEdit ? 'Edit Activity' : 'Activity cannot currently be edited'
            }
          >
            <span>
              <GridActionsCellItem
                icon={<EditOutlinedIcon fontSize="small" />}
                label="Edit Activity"
                disabled={loading || !canEdit}
                onClick={() => this.props.onEditActivity?.(row)}
              />
            </span>
          </Tooltip>,

          <Tooltip
            key="tasks"
            title={canEdit ? 'Plan Tasks' : 'Task planning is currently locked'}
          >
            <span>
              <GridActionsCellItem
                icon={<AccountTreeOutlinedIcon fontSize="small" />}
                label="Plan Tasks"
                disabled={loading || !canEdit}
                onClick={() => this.props.onPlanTasks?.(row)}
              />
            </span>
          </Tooltip>,

          <Tooltip
            key="ready"
            title={
              isReady
                ? 'Activity is already Ready'
                : canReady
                  ? 'Declare Activity Ready'
                  : 'Activity cannot currently be declared Ready'
            }
          >
            <span>
              <GridActionsCellItem
                icon={<TaskAltOutlinedIcon fontSize="small" />}
                label="Declare Ready"
                disabled={loading || isReady || !canReady}
                onClick={() => this.props.onDeclareReady?.(row)}
              />
            </span>
          </Tooltip>,

          <Tooltip
            key="remove"
            title={
              canEdit
                ? 'Remove from Planning Register'
                : 'Activity cannot currently be removed'
            }
          >
            <span>
              <GridActionsCellItem
                icon={<RemoveCircleOutlineIcon fontSize="small" />}
                label="Remove from Planning Register"
                disabled={loading || !canEdit}
                onClick={() => this.props.onRemoveActivity?.(row)}
              />
            </span>
          </Tooltip>,
        ];
      },
    },
  ];

  render() {
    const {
      rows = [],

      loading = false,
    } = this.props;

    return (
      <FDataGrid
        title="Cost Centre Planning Register"
        subtitle={`${rows.length} Activity Plan${
          rows.length === 1 ? '' : 's'
        } • Current Cost Centre and Planning Cycle`}
        rows={rows}
        columns={this.columns()}
        loading={loading}
        height={360}
        getRowId={(row: any) => row.activityplanid ?? row.rid ?? row.recix}
        disableRowSelectionOnClick
        pageSizeOptions={[10, 25, 50]}
        initialState={{
          pagination: {
            paginationModel: {
              pageSize: 10,
            },
          },
        }}
      />
    );
  }
}
